/**
 * TotalVet homepage test cards carousel (port of initTestCardsCarousel in the WP theme's homepage_v3.js).
 * 4 cards per view above 768px, 1 card per view at 768px and below; moves one card at a time, no wrap.
 * Also restarts the muted background videos if the browser paused them while the tab was hidden.
 */
if (!customElements.get('tv-home-tests-carousel')) {
  const GAP = 16;

  class TvHomeTestsCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (!this.viewport || !this.track) return;

      this.items = Array.from(this.track.children);
      this.videos = Array.from(this.track.querySelectorAll('video[autoplay]'));
      this.index = 0;
      this.cardsToShow = this.getCardsToShow();

      this.onPrev = () => this.goTo(this.index - 1);
      this.onNext = () => this.goTo(this.index + 1);
      this.onDot = (event) => this.goTo(Number(event.currentTarget.dataset.dot));
      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.handleResize(), 250);
      };
      this.onVisibility = () => {
        if (document.visibilityState !== 'visible') return;
        this.videos.forEach((video) => {
          if (video.paused) video.play().catch(() => {});
        });
      };
      this.onBlockSelect = (event) => {
        const item = event.target.closest('[data-index]');
        if (item && this.contains(item)) this.goTo(Number(item.dataset.index));
      };

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDot));
      window.addEventListener('resize', this.onResize);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('visibilitychange', this.onVisibility);
      clearTimeout(this.resizeTimer);
    }

    getCardsToShow() {
      return window.innerWidth <= 768 ? 1 : 4;
    }

    getCardWidth() {
      const width = this.viewport.offsetWidth;
      return (width - GAP * (this.cardsToShow - 1)) / this.cardsToShow;
    }

    goTo(index) {
      const last = this.items.length - 1;
      const next = Math.min(Math.max(index, 0), last);
      if (next === this.index) return;
      this.index = next;
      this.update();
    }

    handleResize() {
      const previous = this.cardsToShow;
      this.cardsToShow = this.getCardsToShow();
      if (previous !== this.cardsToShow) {
        this.index = Math.min(Math.max(this.index, 0), this.items.length - 1);
      }
      this.update();
    }

    update() {
      const offset = -(this.index * (this.getCardWidth() + GAP));
      this.track.style.transform = `translateX(${offset}px)`;
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('tv-home-tests-carousel', TvHomeTestsCarousel);
}
